import { useFlag } from '@unleash/proxy-client-react';
import { Dropdown, Slider, Tabs, Tooltip } from 'antd';
import EmojiPicker, { EmojiClickData, SuggestionMode } from 'emoji-picker-react';
import { debounce } from 'lodash';
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Hotkeys from 'react-hot-keys';
import { Editor } from 'slate';
import styled, { createGlobalStyle, css } from 'styled-components';

import { PlusSvg } from '../../assets/icons';
import { BoldSvg } from '../../assets/icons/BoldSvg';
import BorderRadiusSvg from '../../assets/icons/BorderRadiusSvg';
import { BulletsSvg } from '../../assets/icons/BulletsSvg';
import { CenterAlignSvg } from '../../assets/icons/CenterAlignSvg';
import { EmojiSvg } from '../../assets/icons/EmojiSvg';
import FontColorSvg from '../../assets/icons/FontColorSvg';
import { ItalicSvg } from '../../assets/icons/ItalicSvg';
import LayerBackSvg from '../../assets/icons/LayerBackSvg';
import LayerDownSvg from '../../assets/icons/LayerDownSvg';
import LayerFrontSvg from '../../assets/icons/LayerFrontSvg';
import LayerUpSvg from '../../assets/icons/LayerUpSvg';
import { LeftAlignSvg } from '../../assets/icons/LeftAlignSvg';
import LinkSvg from '../../assets/icons/LinkSvg';
import { MagicWandCrossSvg } from '../../assets/icons/MagicWandCrossSvg';
import { MagicWandSvg } from '../../assets/icons/MagicWandSvg';
import MediaIconSvg from '../../assets/icons/MediaIconSvg';
import MinusSvg from '../../assets/icons/MinusSvg';
import { NumberedSvg } from '../../assets/icons/NumberedSvg';
import PadlockSvg from '../../assets/icons/PadlockSvg';
import PadlockOpenSvg from '../../assets/icons/PadlockSvgOpen';
import { RightAlignSvg } from '../../assets/icons/RightAlignSvg';
import { TextSvg } from '../../assets/icons/TextSvg';
import TransparencySvg from '../../assets/icons/TransparencySvg';
import { UnderlineSvg } from '../../assets/icons/UnderlineSvg';
import theme from '../../assets/theme';
import {
  documentColorsSelector,
  selectedSlideSelector,
  useDesignerDataContext,
} from '../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../hooks/useDesignerUIState';
import { useMetrics } from '../../hooks/useMetrics';
import { useOnOutsideClick } from '../../hooks/useOnOutsideClick';
import { notUndefined } from '../../utils/general';
import { zIndexes } from '../../utils/zIndexes';
import { Input } from '../_atoms/_Input';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import {
  insertLink,
  isAlignActive,
  isMarkActive,
  setMark,
  toggleAlign,
  toggleEmojiPicked,
  toggleMark,
  toggleSize,
  toggleType,
} from './richTextEditorUtil';
import { RichTextEditorType } from './SlateRichTextEditor';
import { isValidUrl, parseStringToSlateObject } from './slateUtil';
import { OnElementsChangeProps, Slide, SlideElement } from './slide.types';
import {
  colorIsHexCode,
  isSlideElementText,
  PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME,
  RICHTEXTEDITOR_ID_PREFIX,
} from './slideUtil';

const PreventDeselectMoreSvg = ({
  preventDeselectClassName,
}: {
  preventDeselectClassName?: string;
}) => {
  return (
    <svg
      className={preventDeselectClassName}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className={preventDeselectClassName}
        d="M14 6C14 7.10457 13.1046 8 12 8C10.8954 8 10 7.10457 10 6C10 4.89543 10.8954 4 12 4C13.1046 4 14 4.89543 14 6Z"
        fill="currentColor"
      />
      <path
        className={preventDeselectClassName}
        d="M14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12Z"
        fill="currentColor"
      />
      <path
        className={preventDeselectClassName}
        d="M12 20C13.1046 20 14 19.1046 14 18C14 16.8954 13.1046 16 12 16C10.8954 16 10 16.8954 10 18C10 19.1046 10.8954 20 12 20Z"
        fill="currentColor"
      />
    </svg>
  );
};

const SlideEditorToolbar = ({
  selectedElements,
  onElementsChange,
  editingTextElementId,
  selectedRichTextEditors,
}: {
  selectedElements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  editingTextElementId?: string;
  selectedRichTextEditors: RichTextEditorType[];
}) => {
  const richTextEditor = useDesignerUIContext(c => c.state.richTextEditor);

  const documentColors = useDesignerDataContext(documentColorsSelector);

  const toolbarState = useMemo(() => {
    if (selectedElements.length === 0) return 'empty';
    if (selectedElements.length === 1) {
      const selectedElement = selectedElements[0];
      if (isSlideElementText(selectedElement)) return 'text';
      if (selectedElement.type === 'image' || selectedElement.type === 'generatorImage')
        return 'image';

      if (selectedElement.type === 'shape') return 'shape';
      if (selectedElement.type === 'iframe') return 'iframe';
      if (selectedElement.type === 'svg') return 'svg';
    }
    if (selectedElements.length > 1) {
      const allText = selectedElements.every(isSlideElementText);
      if (allText) {
        return 'multi-text';
      }
      const firstElementType = selectedElements[0].type;
      for (const element of selectedElements) {
        if (element.type !== firstElementType) return 'mixed';
      }
      return `multi-${firstElementType}`;
    }
    return 'empty';
  }, [selectedElements]);
  if (toolbarState == 'empty') return null;
  if (toolbarState === 'text' || toolbarState === 'multi-text')
    return (
      <TextToolbar
        selectedElements={selectedElements}
        onElementsChange={onElementsChange}
        selectedRichTextEditors={selectedRichTextEditors}
        richTextEditor={richTextEditor}
        editingTextElementId={editingTextElementId}
      />
    );
  if (toolbarState === 'image' || toolbarState === 'multi-image')
    return (
      <ImageToolbar
        selectedElements={selectedElements}
        onElementsChange={onElementsChange}
      />
    );

  if (toolbarState === 'iframe')
    return (
      <IframeToolbar
        selectedElements={selectedElements}
        onElementsChange={onElementsChange}
      />
    );
  if (toolbarState === 'svg')
    return (
      <SvgToolbar
        selectedElements={selectedElements}
        onElementsChange={onElementsChange}
        documentColors={documentColors}
      />
    );
  if (toolbarState === 'shape' || toolbarState === 'multi-shape')
    return (
      <ShapeToolbar
        selectedElements={selectedElements}
        onElementsChange={onElementsChange}
        documentColors={documentColors}
      />
    );
  return (
    <MixedToolbar
      selectedElements={selectedElements}
      onElementsChange={onElementsChange}
    />
  );
};

const TextToolbar = ({
  selectedElements,
  onElementsChange,
  richTextEditor,
  editingTextElementId,
  selectedRichTextEditors,
}: {
  selectedElements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  richTextEditor: RichTextEditorType | null;
  selectedRichTextEditors: RichTextEditorType[];
  editingTextElementId?: string;
}) => {
  const { tab } = useDesignerUIContext(c => c.state.selectedTab);
  const noPollOptions = useMemo(() => {
    if (selectedElements.some(e => e.isPollOption)) return false;
    return true;
  }, [selectedElements]);
  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');
  const { TabPane } = Tabs;
  return (
    <ToolbarTabs
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
      activeKey="1"
      tabPosition={'top'}
      style={{ height: 50 }}
      prefixCls={`${PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} ant-tabs`}
      moreIcon={
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '5px 7px',
          }}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        >
          <PreventDeselectMoreSvg
            preventDeselectClassName={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          />
        </div>
      }
    >
      <GlobalStyles />
      {isAITemplatesEnabled &&
        selectedElements.length === 1 &&
        (!!selectedElements[0].promptSourceId || tab === 'prompts') && (
          <TabPane
            tab={
              <AiConnectionAction
                onElementsChange={onElementsChange}
                selectedElement={selectedElements[0]}
                hasDivider={true}
              />
            }
            key={'prompts'}
          ></TabPane>
        )}
      <TabPane
        tab={
          <FontSizeAction
            onElementsChange={onElementsChange}
            selectedRichTextEditors={selectedRichTextEditors}
            elements={selectedElements}
          />
        }
        key={'font-size'}
      ></TabPane>

      <TabPane tab={<ColorTextAction />} key={'text-color'}></TabPane>
      <TabPane
        tab={
          <FormatTextAction
            formatType="bold"
            elements={selectedElements}
            onElementsChange={onElementsChange}
            richTextEditor={richTextEditor}
            hotKey="ctrl+b,command+b"
            selectedRichTextEditors={selectedRichTextEditors}
            editingTextElementId={editingTextElementId}
          />
        }
        key={'bold'}
      ></TabPane>
      <TabPane
        tab={
          <FormatTextAction
            formatType="italic"
            hotKey="ctrl+i,command+i"
            elements={selectedElements}
            onElementsChange={onElementsChange}
            richTextEditor={richTextEditor}
            selectedRichTextEditors={selectedRichTextEditors}
            editingTextElementId={editingTextElementId}
          />
        }
        key={'italic'}
      ></TabPane>
      <TabPane
        tab={
          <FormatTextAction
            formatType="underline"
            hotKey="ctrl+u,command+u"
            elements={selectedElements}
            onElementsChange={onElementsChange}
            richTextEditor={richTextEditor}
            selectedRichTextEditors={selectedRichTextEditors}
            editingTextElementId={editingTextElementId}
            hasDivider={true}
          />
        }
        key={'underline'}
      ></TabPane>
      {editingTextElementId && (
        <TabPane
          tab={
            <LinkAction
              elements={selectedElements}
              richTextEditor={richTextEditor}
              editingTextElementId={editingTextElementId}
            />
          }
          key={'link'}
        ></TabPane>
      )}
      <TabPane
        tab={
          <AlignAction
            elements={selectedElements}
            onElementsChange={onElementsChange}
            richTextEditor={richTextEditor}
            selectedRichTextEditors={selectedRichTextEditors}
            editingTextElementId={editingTextElementId}
          />
        }
        key={'align'}
      ></TabPane>
      {noPollOptions && (
        <TabPane
          tab={
            <ListTextAction
              elements={selectedElements}
              listType="bulleted-list"
              onElementsChange={onElementsChange}
              richTextEditor={richTextEditor}
              selectedRichTextEditors={selectedRichTextEditors}
              editingTextElementId={editingTextElementId}
            />
          }
          key={'bullets'}
        ></TabPane>
      )}

      {noPollOptions && (
        <TabPane
          tab={
            <ListTextAction
              elements={selectedElements}
              listType="numbered-list"
              onElementsChange={onElementsChange}
              richTextEditor={richTextEditor}
              selectedRichTextEditors={selectedRichTextEditors}
              editingTextElementId={editingTextElementId}
              hasDivider={true}
            />
          }
          key={'numbered'}
        ></TabPane>
      )}

      {/* <HorizontalDivider /> */}
      {noPollOptions && (
        <TabPane
          tab={
            <MoveAction elements={selectedElements} onElementsChange={onElementsChange} />
          }
          key={'move'}
        ></TabPane>
      )}
      {noPollOptions && (
        <TabPane
          tab={
            <TransparencyAction
              elements={selectedElements}
              onElementsChange={onElementsChange}
              hasDivider={true}
            />
          }
          key={'transparency'}
        ></TabPane>
      )}
      {editingTextElementId && (
        <TabPane
          tab={
            <EmojiAction
              richTextEditor={richTextEditor}
              elements={selectedElements}
              editingTextElementId={editingTextElementId}
            />
          }
          key={'emoji'}
        ></TabPane>
      )}
      <TabPane
        tab={
          <LockAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'lock'}
      ></TabPane>
    </ToolbarTabs>
  );
};

const GlobalStyles = createGlobalStyle`
.ant-tabs-dropdown.ant-tabs-dropdown-placement-bottomRight {
    z-index: 9999;
  }

   .ant-tabs-dropdown-menu-item {
     min-width: fit-content;
     cursor: default;
     padding:  5px;
   }

   .ant-tabs-dropdown-menu-item-active {
    background-color: ${theme.colors.white} !important;
    padding:  5px;
  }

  .ant-tabs-dropdown-menu-item-active:hover {
    background-color: ${theme.colors.white} !important;
  }

 .ant-tabs-dropdown-menu.ant-tabs-dropdown-menu-root.ant-tabs-dropdown-menu-vertical {
    display: flex;   
  }
  

  `;

const ToolbarTabs = styled(Tabs)<{ $noMargin?: boolean }>`
  color: ${theme.colors.black};
  background-color: ${theme.colors.white};
  font-weight: bold;
  border-radius: 15px;
  overflow: hidden;
  .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 10px;
  }
  .ant-tabs-nav {
    height: 50px;
    width: 100%;
  }

  .ant-tabs-nav-list {
    overflow: hidden;
  }

  .ant-tabs-tab-btn:focus,
  .ant-tabs-tab-remove:focus,
  .ant-tabs-tab-btn:active,
  .ant-tabs-tab-remove:active {
    color: ${theme.colors.black};
  }

  &&-active &-btn {
    text-shadow: 0 0 0.25px currentcolor;
  }
  .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap::after,
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,
  .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap::before {
    box-shadow: none !important;
    color: ${theme.colors.black} !important;
  }

  .ant-tabs-nav::before {
    border-bottom: none !important;
  }

  .ant-tabs-top > .ant-tabs-nav::before,
  .ant-tabs-bottom > .ant-tabs-nav::before,
  .ant-tabs-top > div > .ant-tabs-nav::before,
  .ant-tabs-bottom > div > .ant-tabs-nav::before {
    border-bottom: none !important;
  }
  .ant-tabs-content {
    height: 100% !important;
  }

  .ant-tabs-tab {
    &:hover,
    &:active,
    &:focus {
      color: ${theme.colors.black};
    }
  }

  .ant-tabs-ink-bar {
    background: none;
  }

  .ant-tabs-nav-more {
    padding: 0px !important;
  }

  height: 100%;

  ${({ $noMargin }) =>
    $noMargin &&
    css`
      .ant-tabs-nav {
        margin-bottom: 0px;
      }
    `}
`;

const ImageToolbar = ({
  selectedElements,
  onElementsChange,
}: {
  selectedElements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
}) => {
  const { tab } = useDesignerUIContext(c => c.state.selectedTab);
  const { TabPane } = Tabs;
  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');
  return (
    <ToolbarTabs
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
      activeKey="1"
      tabPosition={'top'}
      style={{ height: 50 }}
      moreIcon={
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '5px 7px',
          }}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        >
          <PreventDeselectMoreSvg
            preventDeselectClassName={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          />
        </div>
      }
    >
      <GlobalStyles />

      {isAITemplatesEnabled &&
        selectedElements.length === 1 &&
        (!!selectedElements[0].promptSourceId || tab === 'prompts') && (
          <TabPane
            tab={
              <AiConnectionAction
                onElementsChange={onElementsChange}
                selectedElement={selectedElements[0]}
                hasDivider={true}
              />
            }
            key={'prompts'}
          ></TabPane>
        )}

      <TabPane
        tab={
          <BorderRadiusButton
            selectedElements={selectedElements}
            onElementsChange={onElementsChange}
          />
        }
        key={'border-radius'}
      ></TabPane>
      <TabPane
        tab={
          <MoveAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'move'}
      ></TabPane>
      <TabPane
        tab={
          <TransparencyAction
            elements={selectedElements}
            onElementsChange={onElementsChange}
          />
        }
        key={'transparency'}
      ></TabPane>
      <TabPane
        tab={
          <LockAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'lock'}
      ></TabPane>
    </ToolbarTabs>
  );
};
const MixedToolbar = ({
  selectedElements,
  onElementsChange,
}: {
  selectedElements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
}) => {
  const showTransparaency = useMemo(() => {
    if (selectedElements.some(e => e.isPollOption)) return false;
    return true;
  }, [selectedElements]);
  const { TabPane } = Tabs;
  return (
    <ToolbarTabs
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
      activeKey="1"
      tabPosition={'top'}
      style={{ height: 50 }}
      moreIcon={
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '5px 7px',
          }}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        >
          <PreventDeselectMoreSvg
            preventDeselectClassName={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          />
        </div>
      }
    >
      <GlobalStyles />
      <TabPane
        tab={
          <MoveAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'move'}
      ></TabPane>
      {showTransparaency && (
        <TabPane
          tab={
            <TransparencyAction
              elements={selectedElements}
              onElementsChange={onElementsChange}
            />
          }
          key={'transparency'}
        ></TabPane>
      )}
      <TabPane
        tab={
          <LockAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'lock'}
      ></TabPane>
    </ToolbarTabs>
  );
};

const IframeToolbar = ({
  selectedElements,
  onElementsChange,
}: {
  selectedElements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
}) => {
  const { TabPane } = Tabs;
  return (
    <ToolbarTabs
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
      activeKey="1"
      tabPosition={'top'}
      style={{ height: 50 }}
      moreIcon={
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '5px 7px',
          }}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        >
          <PreventDeselectMoreSvg
            preventDeselectClassName={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          />
        </div>
      }
    >
      <GlobalStyles />
      {selectedElements.length === 1 && (
        <TabPane
          tab={
            <ChangeMediaAction
              selectedElement={selectedElements[0]}
              onElementsChange={onElementsChange}
            />
          }
          key={'media'}
        ></TabPane>
      )}
      <TabPane
        tab={
          <MoveAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'move'}
      ></TabPane>
      <TabPane
        tab={
          <TransparencyAction
            elements={selectedElements}
            onElementsChange={onElementsChange}
          />
        }
        key={'transparency'}
      ></TabPane>
      <TabPane
        tab={
          <LockAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'lock'}
      ></TabPane>
    </ToolbarTabs>
  );
};

const SvgToolbar = ({
  selectedElements,
  onElementsChange,
}: {
  selectedElements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  documentColors?: string[];
}) => {
  const { TabPane } = Tabs;
  return (
    <ToolbarTabs
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
      activeKey="1"
      tabPosition={'top'}
      style={{ height: 50 }}
      moreIcon={
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '5px 7px',
          }}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        >
          <PreventDeselectMoreSvg
            preventDeselectClassName={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          />
        </div>
      }
    >
      <GlobalStyles />
      {selectedElements.length === 1 &&
        selectedElements[0].colors &&
        selectedElements[0].colors?.length >= 1 && (
          <TabPane
            tab={<ColorSvgAction selectedElement={selectedElements[0]} />}
            key={'color'}
          ></TabPane>
        )}
      <TabPane
        tab={
          <MoveAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'move'}
      ></TabPane>
      <TabPane
        tab={
          <TransparencyAction
            elements={selectedElements}
            onElementsChange={onElementsChange}
          />
        }
        key={'transparency'}
      ></TabPane>
      <TabPane
        tab={
          <LockAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'lock'}
      ></TabPane>
    </ToolbarTabs>
  );
};

const ShapeToolbar = ({
  selectedElements,
  onElementsChange,
  documentColors,
}: {
  selectedElements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  documentColors?: string[];
}) => {
  const { TabPane } = Tabs;
  return (
    <ToolbarTabs
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
      activeKey="1"
      tabPosition={'top'}
      style={{ height: 50 }}
      moreIcon={
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '5px 7px',
          }}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        >
          <PreventDeselectMoreSvg
            preventDeselectClassName={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          />
        </div>
      }
    >
      <GlobalStyles />
      <TabPane
        tab={
          <ColorShapeAction
            elements={selectedElements}
            onElementsChange={onElementsChange}
            documentColors={documentColors}
          />
        }
        key={'color'}
      ></TabPane>
      <TabPane
        tab={
          <MoveAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'move'}
      ></TabPane>
      <TabPane
        tab={
          <TransparencyAction
            elements={selectedElements}
            onElementsChange={onElementsChange}
          />
        }
        key={'transparency'}
      ></TabPane>
      <TabPane
        tab={
          <LockAction elements={selectedElements} onElementsChange={onElementsChange} />
        }
        key={'lock'}
      ></TabPane>
    </ToolbarTabs>
  );
};

// ----- ACTIONS ------

const LockAction = ({
  elements,
  onElementsChange,
}: {
  elements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
}) => {
  const isLocked = useMemo(() => {
    for (const element of elements) {
      if (element.lockedPosition) return true;
    }
    return false;
  }, [elements]);

  const isDisabled = useMemo(() => {
    if (elements.some(e => e.isPollOption)) return true;
    if (elements.some(e => e.lockedFromDeletion)) return true;
    return false;
  }, [elements]);
  const handleLockClick = useCallback(() => {
    const updatedElements: SlideElement[] = elements.map(element => ({
      ...element,
      lockedPosition: !isLocked,
    }));
    onElementsChange({ updatedElements: updatedElements });
  }, [elements, isLocked, onElementsChange]);
  return (
    <ToolbarButton
      isDisabled={isDisabled}
      metric={'Creator.Toolbar.Lock'}
      onClick={handleLockClick}
      icon={isLocked ? <PadlockSvg /> : <PadlockOpenSvg />}
      tooltip={
        isDisabled
          ? 'Locked element'
          : isLocked
          ? 'Unlock this element'
          : 'Lock this element'
      }
    />
  );
};

const ColorShapeAction = ({
  elements,
}: {
  elements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  documentColors?: string[];
}) => {
  const currentColor = useMemo(() => {
    if (elements.length === 0) return;
    const firstElement = elements[0];
    if (elements.length === 1) {
      return firstElement.colors?.[0];
    }
    const firstColor = firstElement.colors?.[0];
    for (const element of elements) {
      if (element.colors?.[0] !== firstColor) return 'mixed';
    }
    return firstColor;
  }, [elements]);

  const dispatch = useDesignerUIContext(c => c.dispatch);
  const handleColorClick = useCallback(() => {
    dispatch({
      type: DesignerUIStateActions.setSelectedTab,
      payload: {
        tab: 'colorPicker',
        extraState: 'shape',
      },
    });
  }, [dispatch]);

  return (
    <ToolbarButtonContainer className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}>
      <PreventUnselectOverlay
        className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        onClick={handleColorClick}
        onTouchEnd={handleColorClick}
      />
      <IconWrapper height="20px" width="20px">
        <ColorElementItem $color={currentColor} />
      </IconWrapper>
    </ToolbarButtonContainer>
  );
};
const ColorSvgAction = ({ selectedElement }: { selectedElement: SlideElement }) => {
  const selectedTab = useDesignerUIContext(context => context.state.selectedTab);
  const selectedIndex = useMemo(() => {
    if (selectedTab.tab !== 'colorPicker') return false;
    try {
      const colorIndex = parseInt(selectedTab.extraState || '');
      return colorIndex;
    } catch (error) {}
  }, [selectedTab]);
  if (selectedElement.colors?.length === 0) return null;
  return (
    <>
      <ColorScrollContainer>
        <ColorsContainer>
          {selectedElement.colors?.map((color: string, index: number) => (
            <ColorSvgItem
              isSelected={selectedIndex === index}
              color={color}
              key={index}
              index={index}
            />
          ))}
        </ColorsContainer>
      </ColorScrollContainer>
    </>
  );
};

const ColorScrollContainer = styled.div`
  max-width: 150px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;
const ColorsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: max-content;
`;
const ColorSvgItem = ({
  color,
  index,
  isSelected,
}: {
  color?: string;
  isSelected?: boolean;
  index: number;
}) => {
  const currentColor = useMemo(() => {
    if (!color) return;
    if (color === 'none') return;
    if (colorIsHexCode(color)) return color;
    return color;
  }, [color]);
  const dispatch = useDesignerUIContext(c => c.dispatch);
  const handleColorClick = useCallback(() => {
    dispatch({
      type: DesignerUIStateActions.setSelectedTab,
      payload: {
        tab: 'colorPicker',
        extraState: `${index}`,
      },
    });
  }, [dispatch, index]);

  return (
    <>
      <ToolbarButtonContainer
        className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        $isActive={isSelected}
      >
        <PreventUnselectOverlay
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          onClick={handleColorClick}
        />
        <IconWrapper height="20px" width="20px">
          <ColorElementItem $color={currentColor} />
        </IconWrapper>
      </ToolbarButtonContainer>
    </>
  );
};

const ColorElementItem = styled.div<{ $color?: string }>`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 1px solid ${theme.colors.grey};
  background-color: ${({ $color }) => $color};
  &:hover {
    cursor: pointer;
  }
`;

export const AiConnectionAction = ({
  selectedElement,
  onElementsChange,
  hasDivider,
  isMiniToolbar,
}: {
  selectedElement: SlideElement;
  onElementsChange: (props: OnElementsChangeProps) => void;
  hasDivider?: boolean;
  isMiniToolbar?: boolean;
}) => {
  const metrics = useMetrics();
  const aiConnection = useDesignerUIContext(c => c.state.aiConnection);
  const isActive = useMemo(() => {
    return aiConnection?.aiTextElementId === selectedElement.id;
  }, [aiConnection?.aiTextElementId, selectedElement.id]);
  const dispatch = useDesignerUIContext(context => context.dispatch);

  const handleAiConnectionClear = useCallback(
    (elementsToBeCleared: SlideElement[]) => {
      const updatedElements: SlideElement[] = elementsToBeCleared
        .map(elementToClear => {
          if (elementToClear.isPollOption && elementToClear.promptSourceId) {
            const updatedElement: SlideElement = {
              ...elementToClear,
              promptSourceId: undefined,
            };
            return updatedElement;
          }
          if (elementToClear.type === 'generatorImage') {
            const updatedElement: SlideElement = {
              ...elementToClear,
              promptSourceId: undefined,
              type: 'image',
            };
            return updatedElement;
          }
          if (elementToClear.type === 'generatorText') {
            const updatedElement: SlideElement = {
              ...elementToClear,
              promptSourceId: undefined,
              type: 'text',
            };

            return updatedElement;
          }
        })
        .filter(notUndefined);
      onElementsChange({ updatedElements });
    },
    [onElementsChange],
  );
  const handleAiConnectionSelected = useCallback(() => {
    metrics.logEvent('Creator.Toolbar.AiConnect');
    if (
      selectedElement.type === 'generatorText' ||
      selectedElement.type === 'generatorImage'
    ) {
      handleAiConnectionClear([selectedElement]);
      return;
    }
    if (isActive) {
      dispatch({
        type: DesignerUIStateActions.clearAiElements,
      });
      return;
    }

    if (selectedElement.type === 'text') {
      dispatch({
        type: DesignerUIStateActions.setAITextElementId,
        elementId: selectedElement.id,
        isPollOption: selectedElement.isPollOption,
      });
      return;
    }

    if (selectedElement.type === 'image') {
      dispatch({
        type: DesignerUIStateActions.setAIImageElementId,
        elementId: selectedElement.id,
      });
      return;
    }
  }, [dispatch, handleAiConnectionClear, isActive, metrics, selectedElement]);

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <ToolbarButtonContainer
        className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        $isActive={false}
        style={{ width: isMiniToolbar ? 15 : 30, height: isMiniToolbar ? 15 : 30 }}
      >
        <ToolbarButton
          onClick={handleAiConnectionSelected}
          metric={'Creator.Toolbar.AiConnect'}
          isMiniToolbar={isMiniToolbar}
          iconHeight={isMiniToolbar ? 12 : 15}
          iconWidth={isMiniToolbar ? 12 : 15}
          style={
            isActive ||
            selectedElement.type === 'generatorText' ||
            selectedElement.type === 'generatorImage'
              ? {
                  backgroundColor: theme.colors.purple,
                  color: theme.colors.white,
                  width: isMiniToolbar ? 15 : 30,
                  height: isMiniToolbar ? 15 : 30,
                }
              : { width: isMiniToolbar ? 15 : 30, height: isMiniToolbar ? 15 : 30 }
          }
          icon={
            selectedElement.type === 'generatorText' ||
            selectedElement.type === 'generatorImage' ? (
              <MagicWandCrossSvg />
            ) : (
              <MagicWandSvg />
            )
          }
          tooltip={'Connect with AI'}
        />
      </ToolbarButtonContainer>
      {hasDivider && <RightSideVerticalDivider />}
    </div>
  );
};

const ChangeMediaAction = ({
  selectedElement,
}: {
  selectedElement: SlideElement;
  onElementsChange: (props: OnElementsChangeProps) => void;
}) => {
  const handleMediaSelected = useDesignerUIContext(c => c.handleMediaSelected);
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const handleChangeMediaAction = useCallback(() => {
    if (selectedElement.type === 'iframe') {
      dispatch({
        type: DesignerUIStateActions.setSelectedTab,
        payload: { tab: 'media', subTab: 'appSmash' },
      });
    } else if (selectedElement.type === 'image') {
      const payload =
        selectedElement.mediaMimetype && selectedElement.mediaFileId
          ? {
              src: selectedElement.mediaFileId,
              mimeType: selectedElement.mediaMimetype,
            }
          : undefined;
      handleMediaSelected(payload);
    }
  }, [selectedElement, dispatch, handleMediaSelected]);

  return (
    <>
      <ToolbarButton
        onClick={handleChangeMediaAction}
        metric={'Creator.Toolbar.ChangeMedia'}
        icon={<MediaIconSvg />}
        tooltip={'Change media'}
      />
    </>
  );
};

const MoveAction = ({
  elements,
  onElementsChange,
}: {
  elements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
}) => {
  const metrics = useMetrics();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  useOnOutsideClick(dropdownRef, () => setIsOpen(false));
  const handleMoveClick = useCallback(
    (zChange: 'up' | 'down' | 'front' | 'back') => {
      onElementsChange({ updatedElements: elements, zChange });
    },
    [elements, onElementsChange],
  );

  const menu = (
    <DropdownMenu ref={dropdownRef}>
      <DropdownMenuItem icon={<LayerBackSvg />} onClick={() => handleMoveClick('back')}>
        <PreventUnselectOverlay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
        To back
      </DropdownMenuItem>
      <DropdownMenuItem icon={<LayerDownSvg />} onClick={() => handleMoveClick('down')}>
        <PreventUnselectOverlay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
        Backward
      </DropdownMenuItem>
      <DropdownMenuItem icon={<LayerUpSvg />} onClick={() => handleMoveClick('up')}>
        <PreventUnselectOverlay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
        Forward
      </DropdownMenuItem>
      <DropdownMenuItem icon={<LayerFrontSvg />} onClick={() => handleMoveClick('front')}>
        <PreventUnselectOverlay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
        To front
      </DropdownMenuItem>
    </DropdownMenu>
  );

  return (
    <ToolbarButtonContainer className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}>
      <Dropdown overlay={menu} placement="bottomCenter" visible={isOpen}>
        <a>
          <Tooltip title={'Move forward and backward'} mouseEnterDelay={1}>
            <PreventUnselectOverlay
              className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
              onTouchEnd={() => {
                setIsOpen(e => !e);
                metrics.logEvent('Creator.Toolbar.LayerUpDown');
              }}
              onClick={() => {
                setIsOpen(e => !e);
                metrics.logEvent('Creator.Toolbar.LayerUpDown');
              }}
            />
          </Tooltip>
          <IconWrapper>
            <LayerBackSvg />
          </IconWrapper>
        </a>
      </Dropdown>
    </ToolbarButtonContainer>
  );
};

const AlignAction = ({
  elements,
  onElementsChange,
  richTextEditor,
  selectedRichTextEditors,
  editingTextElementId,
}: {
  elements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;

  richTextEditor: RichTextEditorType | null;
  selectedRichTextEditors: RichTextEditorType[];
  editingTextElementId?: string;
}) => {
  const dropdownRef = useRef(null);
  const metrics = useMetrics();
  const [isOpen, setIsOpen] = useState(false);
  useOnOutsideClick(dropdownRef, () => setIsOpen(false));
  const activeActiveType = useMemo(() => {
    const firstElement = elements?.[0];
    if (!firstElement) return;
    if (firstElement.type !== 'text') return;
    const editor = selectedRichTextEditors.find(
      e => e.id === `${RICHTEXTEDITOR_ID_PREFIX}${firstElement.id}`,
    );
    if (!editor) return false;
    if (isAlignActive(editor, 'left')) return 'left';
    if (isAlignActive(editor, 'center')) return 'center';
    if (isAlignActive(editor, 'right')) return 'right';
    return;
  }, [elements, selectedRichTextEditors]);

  const handleAlignClick = useCallback(
    (alignType: 'left' | 'center' | 'right') => {
      metrics.logEvent('Creator.SlateToolbar.MarkButton', { align: alignType });
      if (elements.length === 0) return;
      if (richTextEditor && editingTextElementId) {
        // if you are editing an text element then slate will handle the saving
        toggleAlign(richTextEditor, alignType);
        return;
      }
      // if you have selected multiple elements then we need to update all of them
      const updatedElements = [];
      for (const element of elements) {
        const editor = selectedRichTextEditors.find(
          e => e.id === `${RICHTEXTEDITOR_ID_PREFIX}${element.id}`,
        );
        if (!editor) continue;
        toggleAlign(editor, alignType);
        const updatedChildren = JSON.stringify(editor.children);
        const updatedElement: SlideElement = {
          ...element,
          textValue: updatedChildren,
        };
        updatedElements.push(updatedElement);
      }

      onElementsChange({
        updatedElements: updatedElements,
      });
    },
    [
      metrics,
      elements,
      richTextEditor,
      onElementsChange,
      editingTextElementId,
      selectedRichTextEditors,
    ],
  );
  const menu = (
    <DropdownMenu className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} ref={dropdownRef}>
      <DropdownMenuItem
        icon={<LeftAlignSvg />}
        onClick={e => {
          e.preventDefault();
          handleAlignClick('left');
        }}
      >
        <PreventUnselectOverlay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
        Align left
      </DropdownMenuItem>
      <DropdownMenuItem
        icon={<CenterAlignSvg />}
        onClick={() => handleAlignClick('center')}
      >
        <PreventUnselectOverlay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
        Align center
      </DropdownMenuItem>
      <DropdownMenuItem
        icon={<RightAlignSvg />}
        onClick={() => handleAlignClick('right')}
      >
        <PreventUnselectOverlay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
        Align right
      </DropdownMenuItem>
    </DropdownMenu>
  );

  return (
    <ToolbarButtonContainer
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
      $isActive={false}
    >
      <Dropdown overlay={menu} placement="bottomCenter" visible={isOpen}>
        <a>
          <PreventUnselectOverlay
            className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
            onClick={() => {
              setIsOpen(true);
            }}
            onTouchEnd={() => {
              setIsOpen(true);
            }}
          />
          <IconWrapper>
            {activeActiveType === 'left' && <LeftAlignSvg />}
            {activeActiveType === 'center' && <CenterAlignSvg />}
            {activeActiveType === 'right' && <RightAlignSvg />}
            {!activeActiveType && <LeftAlignSvg />}
          </IconWrapper>
        </a>
      </Dropdown>
    </ToolbarButtonContainer>
  );
};

const LinkAction = ({
  elements,
  richTextEditor,
  editingTextElementId,
}: {
  elements: SlideElement[];
  richTextEditor: RichTextEditorType | null;
  editingTextElementId?: string;
}) => {
  const dropdownRef = useRef(null);
  const [newLink, setNewLink] = useState<string>('');
  const [newLinkText, setNewLinkText] = useState<string>('');
  const metrics = useMetrics();
  const [isOpen, setIsOpen] = useState(false);

  const isLinkValid = useMemo(() => {
    // checks if its a valid url with http or https
    if (isValidUrl(newLink)) return true;

    // checks if it ends with a valid domain
    const regex = /^((https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*))$/;
    if (regex.test(newLink)) return true;
  }, [newLink]);
  const onAddLink = useCallback(() => {
    metrics.logEvent('Creator.SlateToolbar.AddLink', {
      linkUrl: newLink,
    });

    if (elements.length === 0) return;
    let linkToAdd = newLink.trimStart(); //remove any trailing white space

    if (
      linkToAdd.startsWith('https') === false ||
      linkToAdd.startsWith('http') === false
    ) {
      linkToAdd = `https://${linkToAdd}`;
    }
    const linkTextToAdd = newLinkText || newLink.slice(0, 20);
    if (richTextEditor && editingTextElementId) {
      // if you are editing an text element then slate will handle the saving
      insertLink(richTextEditor, linkToAdd, linkTextToAdd);
    }
    setIsOpen(false);
  }, [editingTextElementId, elements, metrics, newLink, newLinkText, richTextEditor]);
  useOnOutsideClick(dropdownRef, () => setIsOpen(false));
  const handleOpenMenu = useCallback(() => {
    if (richTextEditor && richTextEditor.selection) {
      const selectedText = Editor.string(richTextEditor, richTextEditor.selection);
      setNewLinkText(selectedText);
    }
    setIsOpen(true);
  }, [richTextEditor]);
  const menu = (
    <AddLinkContainer>
      <Input
        icon={
          <IconWrapper>
            {' '}
            <LinkSvg />
          </IconWrapper>
        }
        placeholder="Add a hyper link or url"
        value={newLink}
        onChange={e => setNewLink(e.target.value)}
      />
      <Input
        icon={
          <IconWrapper>
            <TextSvg />
          </IconWrapper>
        }
        value={newLinkText}
        placeholder={newLink || 'Link text'}
        onChange={e => setNewLinkText(e.target.value)}
      />
      <Button disabled={!isLinkValid} styles={{ width: '100%' }} onClick={onAddLink}>
        Add link
      </Button>
    </AddLinkContainer>
  );

  return (
    <ToolbarButtonContainer
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
      $isActive={false}
    >
      <Dropdown overlay={menu} placement="bottomCenter" visible={isOpen}>
        <a>
          <PreventUnselectOverlay
            className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
            onClick={() => {
              handleOpenMenu();
            }}
            onTouchEnd={() => {
              setIsOpen(true);
            }}
          />
          <IconWrapper>
            <LinkSvg />
          </IconWrapper>
        </a>
      </Dropdown>
    </ToolbarButtonContainer>
  );
};

const AddLinkContainer = styled.div`
  border-radius: 10px;
  background-color: ${theme.colors.white};
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  width: 250px;
  padding: 10px;
  display: flex;
  gap: 10px;
  flex-direction: column;
  align-items: center;
`;
const DropdownMenu = styled.div`
  border-radius: 10px;
  background-color: ${theme.colors.white};
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  width: 150px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DropdownMenuItem = styled(Button)`
  display: flex;
  justify-content: start;
  font-weight: bold;
  width: 100%;
  gap: 10px;
  padding: 5px;
  border-radius: 5px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};

  &:focus {
    background-color: ${theme.colors.white};
    color: ${theme.colors.black};
  }

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.grey};
    color: ${theme.colors.black};
  }
  &:active {
    cursor: pointer;
    background-color: ${theme.colors.greyLight};
  }

  svg {
    height: 15px;
    width: 15px;
  }
`;

const TransparencyAction = ({
  elements,
  onElementsChange,
  hasDivider,
}: {
  elements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  hasDivider?: boolean;
}) => {
  const metrics = useMetrics();
  const selectedSlide = useDesignerDataContext(selectedSlideSelector);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [transparencyValue, setTransparencyValue] = useState<number>(100);
  useOnOutsideClick(dropdownRef, () => setIsOpen(false));
  useEffect(() => {
    if (!elements || elements.length === 0) return;

    const res = elements.reduce(
      (acc, element) => {
        if (element.opacity !== undefined && element.opacity !== acc) {
          acc = element.opacity;
        }
        return acc;
      },
      elements?.[0].opacity !== undefined ? elements?.[0].opacity : 1,
    );
    setTransparencyValue(res * 100);
  }, [elements]);
  const debounceTransparencyChange = useCallback(
    debounce((updatedElements: SlideElement[], slide: Slide) => {
      onElementsChange({
        updatedElements: updatedElements,
        skipUpdateSelectedElements: true,
        slide,
      });
    }, 20),
    [],
  );
  const handleTransparencyChanged = useCallback(
    (value: number, slide: Slide) => {
      const updatedElements = [];
      for (const element of elements) {
        const updatedElement: SlideElement = {
          ...element,
          opacity: value / 100,
        };
        updatedElements.push(updatedElement);
      }
      debounceTransparencyChange(updatedElements, slide);
      setTransparencyValue(value);
    },

    [debounceTransparencyChange, elements],
  );
  const menu = (
    <SliderMenu className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} ref={dropdownRef}>
      <span style={{ marginTop: '5px' }}>Transparency</span>
      <Slider
        style={{ width: '100%' }}
        value={transparencyValue}
        min={0}
        max={100}
        onChange={e => {
          if (!selectedSlide) return;
          handleTransparencyChanged(e, selectedSlide);
        }}
        handleStyle={{
          borderColor: theme.colors.orange,
        }}
        trackStyle={{
          backgroundColor: theme.colors.orange,
        }}
      />
    </SliderMenu>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <ToolbarButtonContainer
        className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        $isActive={false}
      >
        <Dropdown overlay={menu} placement="bottomCenter" visible={isOpen}>
          <a>
            <Tooltip title={'Change transparency'} mouseEnterDelay={1}>
              <PreventUnselectOverlay
                className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
                onTouchEnd={e => {
                  setIsOpen(true);
                  metrics.logEvent('Creator.Toolbar.Transparency');
                }}
                onClick={() => {
                  setIsOpen(true);
                  metrics.logEvent('Creator.Toolbar.Transparency');
                }}
              />
            </Tooltip>
            <IconWrapper>
              <TransparencySvg />
            </IconWrapper>
          </a>
        </Dropdown>
      </ToolbarButtonContainer>
      {hasDivider && <RightSideVerticalDivider />}
    </div>
  );
};

const SliderMenu = styled.div`
  border-radius: 10px;
  background-color: ${theme.colors.white};
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  width: 150px;
  padding: 5px 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormatTextAction = ({
  elements,
  onElementsChange,
  formatType,
  richTextEditor,
  editingTextElementId,
  selectedRichTextEditors,
  hotKey,
  hasDivider,
}: {
  elements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  selectedRichTextEditors: RichTextEditorType[];
  formatType: 'bold' | 'italic' | 'underline';
  richTextEditor: RichTextEditorType | null;
  editingTextElementId?: string;
  hotKey?: string;
  hasDivider?: boolean;
}) => {
  const handleFormatClick = useCallback(() => {
    if (elements.length === 0) return;

    if (elements.length === 1 && richTextEditor && editingTextElementId) {
      toggleMark(richTextEditor, formatType);
      // if you are editing an text element then slate will handle the saving
      return;
    }

    // if you have selected multiple elements then we need to update all of them
    const updatedElements = [];
    for (const element of elements) {
      const editor = selectedRichTextEditors.find(
        e => e.id === `${RICHTEXTEDITOR_ID_PREFIX}${element.id}`,
      );
      if (!editor) continue;
      const isActive = isMarkActive(editor, formatType);
      setMark(editor, formatType, !isActive);
      const updatedChildren = JSON.stringify(editor.children);
      const updatedElement: SlideElement = {
        ...element,
        textValue: updatedChildren,
      };
      updatedElements.push(updatedElement);
    }

    onElementsChange({ updatedElements: updatedElements });
  }, [
    editingTextElementId,
    elements,
    formatType,
    onElementsChange,
    richTextEditor,
    selectedRichTextEditors,
  ]);

  const icon = useMemo(() => {
    if (formatType === 'bold') return <BoldSvg />;
    if (formatType === 'italic') return <ItalicSvg />;
    if (formatType === 'underline') return <UnderlineSvg />;
    return <></>;
  }, [formatType]);
  const metric = useMemo(() => {
    if (formatType === 'bold') return 'Creator.Toolbar.Bold';
    if (formatType === 'italic') return 'Creator.Toolbar.Italic';
    if (formatType === 'underline') return 'Creator.Toolbar.Underline';
    return;
  }, [formatType]);
  const tooltip = useMemo(() => {
    if (formatType === 'bold') return 'Format bold';
    if (formatType === 'italic') return 'Format italic';
    if (formatType === 'underline') return 'Format underline';
    return '';
  }, [formatType]);
  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      {hotKey && (
        <Hotkeys
          keyName={hotKey || ''}
          allowRepeat={true}
          onKeyDown={(_, evtn) => {
            evtn.preventDefault();
            handleFormatClick();
          }}
        />
      )}
      <ToolbarButton
        icon={icon}
        tooltip={tooltip}
        metric={metric}
        onClick={handleFormatClick}
        preventDefault={true}
      />
      {hasDivider && <RightSideVerticalDivider />}
    </div>
  );
};
const ListTextAction = ({
  elements,
  onElementsChange,
  richTextEditor,
  listType,
  editingTextElementId,
  selectedRichTextEditors,
  hasDivider,
}: {
  elements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  selectedRichTextEditors: RichTextEditorType[];
  richTextEditor: RichTextEditorType | null;
  listType: 'numbered-list' | 'bulleted-list';
  editingTextElementId?: string;
  hasDivider?: boolean;
}) => {
  const handleListTypeClick = useCallback(() => {
    if (elements.length === 0) return;

    if (elements.length === 1 && richTextEditor && editingTextElementId) {
      toggleType({ editor: richTextEditor, format: listType, formatAll: true });
      // if you are editing an text element then slate will handle the saving
      return;
    }

    // if you have selected multiple elements then we need to update all of them
    const updatedElements = [];
    for (const element of elements) {
      const editor = selectedRichTextEditors.find(
        e => e.id === `${RICHTEXTEDITOR_ID_PREFIX}${element.id}`,
      );
      if (!editor) continue;
      toggleType({ editor, format: listType, formatAll: true });
      const updatedChildren = JSON.stringify(editor.children);
      const updatedElement: SlideElement = {
        ...element,
        textValue: updatedChildren,
      };
      updatedElements.push(updatedElement);
    }

    onElementsChange({ updatedElements: updatedElements });
  }, [
    editingTextElementId,
    elements,
    listType,
    onElementsChange,
    richTextEditor,
    selectedRichTextEditors,
  ]);

  const icon = useMemo(() => {
    if (listType === 'bulleted-list') return <BulletsSvg />;
    if (listType === 'numbered-list') return <NumberedSvg />;
    return <></>;
  }, [listType]);
  const metric = useMemo(() => {
    if (listType === 'bulleted-list') return 'Creator.Toolbar.Bullets';
    if (listType === 'numbered-list') return 'Creator.Toolbar.Numbered';
    return;
  }, [listType]);
  const tooltip = useMemo(() => {
    if (listType === 'bulleted-list') return 'Bulleted list';
    if (listType === 'numbered-list') return 'Numbered list';
    return '';
  }, [listType]);
  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <ToolbarButton
        icon={icon}
        tooltip={tooltip}
        onClick={handleListTypeClick}
        metric={metric}
        preventDefault={true}
      />
      {hasDivider && <RightSideVerticalDivider />}
    </div>
  );
};

const BorderRadiusButton = ({
  selectedElements,
  onElementsChange,
}: {
  selectedElements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
}) => {
  const metrics = useMetrics();
  const [selectedBorderRadius, setSelectedBorderRadius] = useState(
    selectedElements?.[0]?.borderRadius || 0,
  );
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(dropdownRef, () => setIsOpen(false));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceBorderRadiusChange = useCallback(
    debounce((updatedElements: SlideElement[]) => {
      onElementsChange({ updatedElements: updatedElements });
    }, 20),
    [],
  );
  const handleBordeRadiusClick = useCallback(
    (newBorderRadius: number) => {
      const updatedElements = selectedElements.map(element => ({
        ...element,
        borderRadius: newBorderRadius,
      }));
      debounceBorderRadiusChange(updatedElements);
      setSelectedBorderRadius(newBorderRadius);
    },
    [debounceBorderRadiusChange, selectedElements],
  );
  const bordeRadiusDropdown = useCallback(
    () => (
      <BordeRadiusContainer
        className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
        ref={dropdownRef}
      >
        Border radius
        <Slider
          value={selectedBorderRadius}
          min={0}
          style={{ width: '100%' }}
          max={50}
          step={1}
          trackStyle={{ backgroundColor: theme.colors.orange }}
          handleStyle={{
            borderColor: theme.colors.orange,
            backgroundColor: theme.colors.white,
          }}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          onChange={(value: number) => handleBordeRadiusClick(value)}
        />
      </BordeRadiusContainer>
    ),
    [handleBordeRadiusClick, selectedBorderRadius],
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <ToolbarButtonContainer>
        <Dropdown
          overlay={bordeRadiusDropdown}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          disabled={selectedElements.length === 0}
          visible={isOpen}
          placement={'bottomLeft'}
        >
          <a>
            <Tooltip title={'Adjust border radius'} mouseEnterDelay={1}>
              <PreventUnselectOverlay
                className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
                onTouchEnd={() => {
                  setIsOpen(e => !e);
                  metrics.logEvent('Creator.Toolbar.BorderRadius');
                }}
                onClick={() => {
                  setIsOpen(true);
                  metrics.logEvent('Creator.Toolbar.BorderRadius');
                }}
              />
            </Tooltip>
            <IconWrapper>
              <BorderRadiusSvg />
            </IconWrapper>
          </a>
        </Dropdown>
      </ToolbarButtonContainer>
      <RightSideVerticalDivider />
    </div>
  );
};

const BordeRadiusContainer = styled.div`
  width: 150px;
  background-color: ${theme.colors.white};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  font-weight: bold;
  padding: 10px;
  transform: translateX(-50px);
  box-shadow: 0px 4px 30px rgba(222, 157, 163, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FontSizeAction = ({
  elements,
  onElementsChange,
  selectedRichTextEditors,
}: {
  elements: SlideElement[];
  onElementsChange: (props: OnElementsChangeProps) => void;
  selectedRichTextEditors: RichTextEditorType[];
}) => {
  const [activeSize, setActiveSize] = useState<undefined | number>(undefined);
  const [selectedElementIds, setSelectedElementsIds] = useState<string[]>([]);
  const elementTransformPropperies = useDesignerUIContext(
    c => c.state.elementTransformationProperties,
  );

  const handleActiveSizeChange = useCallback(
    (newSize?: number) => {
      if (newSize === undefined) {
        setActiveSize(undefined);
        return;
      }
      const elementScale =
        elementTransformPropperies?.[0]?.transformScale || elements[0].scale || 1;

      setActiveSize(newSize * elementScale);
    },
    [elementTransformPropperies, elements],
  );
  useEffect(() => {
    const selectedElementsIds = elements.map(e => e.id);
    if (
      JSON.stringify(selectedElementsIds) === JSON.stringify(selectedElementIds) &&
      !elementTransformPropperies?.[0]?.transformScale
    )
      return;
    setSelectedElementsIds(selectedElementsIds);
    if (elements.length === 0) return;
    if (elements.length >= 1) {
      let elementTextValue = undefined;

      const firstElement = elements[0];

      if (!elementTextValue) {
        elementTextValue = firstElement.textValue;
      }
      const slateObjects = parseStringToSlateObject(elementTextValue);
      const firstElementSize = slateObjects?.[0]?.size;
      let elementSizeToSet = slateObjects.reduce((acc, slateObject) => {
        if (slateObject.size !== acc) {
          acc = undefined;
        }
        return acc;
      }, firstElementSize);
      if (elements.length === 1 || !elementSizeToSet) {
        handleActiveSizeChange(elementSizeToSet);
        return;
      }

      for (const element of elements) {
        const editor = selectedRichTextEditors.find(
          e => e.id === `${RICHTEXTEDITOR_ID_PREFIX}${element.id}`,
        );
        if (!editor) continue;
        const slateObjects = parseStringToSlateObject(elementTextValue);
        const elementSize = slateObjects.reduce((acc, slateObject) => {
          if (slateObject.size !== acc) {
            acc = undefined;
          }
          return acc;
        }, firstElementSize);

        if (!elementSize || elementSize !== elementSizeToSet) {
          elementSizeToSet = undefined;
        }
      }
      handleActiveSizeChange(elementSizeToSet);
    }
  }, [
    elementTransformPropperies,
    elements,
    handleActiveSizeChange,
    selectedElementIds,
    selectedRichTextEditors,
  ]);

  const handleFontSizeChange = useCallback(
    ({
      newValue,
      increaseDecrease,
    }: {
      newValue?: number;
      increaseDecrease?: 'increase' | 'decrease';
    }) => {
      const updatedElements = [];
      if (elements.length === 0) return;
      let firstElementSize = undefined;
      // we have more element selected;
      for (const element of elements) {
        const editor = selectedRichTextEditors.find(
          e => e.id === `${RICHTEXTEDITOR_ID_PREFIX}${element.id}`,
        );
        if (!editor) continue;
        let newTextValue = JSON.stringify(editor.children);
        let newSizeValue;
        const elementScale =
          elementTransformPropperies?.[0]?.transformScale || element.scale || 1;

        if (increaseDecrease === 'increase' || increaseDecrease === 'decrease') {
          const { newSize } = toggleSize({
            editor: editor,
            increaseOrDecrease: increaseDecrease,
            scale: elementScale,
          });
          newTextValue = JSON.stringify(editor.children);

          newSizeValue = newSize;
        } else {
          if (!newValue) {
            handleActiveSizeChange(undefined);
            return;
          }
          const newSizeParsed = newValue;
          if (isNaN(newSizeParsed)) {
            return;
          }

          const { newSize } = toggleSize({
            editor: editor,
            size: newSizeParsed,
          });

          newTextValue = JSON.stringify(editor.children);
          newSizeValue = newSize / elementScale;
        }

        const updatedElement: SlideElement = {
          ...element,
          textValue: newTextValue,
          scale: 1,
          width: element.width,
          height: element.height,
        };
        updatedElements.push(updatedElement);
        if (element.id === elements[0].id) {
          firstElementSize = newSizeValue;
        }
        if (firstElementSize !== newSizeValue) {
          firstElementSize = undefined;
        }
      }
      onElementsChange({ updatedElements: updatedElements });
      setActiveSize(firstElementSize);
    },
    [
      elementTransformPropperies,
      elements,
      handleActiveSizeChange,
      onElementsChange,
      selectedRichTextEditors,
    ],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <ToolbarButton
        icon={<MinusSvg />}
        onClick={() => handleFontSizeChange({ increaseDecrease: 'decrease' })}
        preventDefault={true}
        metric={'Creator.Toolbar.FontSizeDecrease'}
        isActive={false}
        tooltip={'Decrease font size'}
      />
      <SizeContainer>
        <SizeInput
          value={activeSize ? Math.floor(activeSize) : ''}
          placeholder={'- -'}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              if (activeSize !== undefined)
                handleFontSizeChange({ newValue: activeSize });
            }
          }}
          type={'text'}
          onBlur={() => {
            if (activeSize !== undefined) handleFontSizeChange({ newValue: activeSize });
          }}
          onChange={e => {
            setActiveSize(parseInt(e.target.value) as number);
          }}
        />
      </SizeContainer>
      <ToolbarButton
        icon={<PlusSvg />}
        metric={'Creator.Toolbar.FontSizeIncrease'}
        onClick={() => handleFontSizeChange({ increaseDecrease: 'increase' })}
        preventDefault={true}
        tooltip={'Increase font size'}
        isActive={false}
      />
      <RightSideVerticalDivider />
    </div>
  );
};

const SizeInput = styled.input`
  width: 100%;
  border: 1px solid ${theme.colors.greyLight};
  text-align: center;
`;

const SizeContainer = styled.div`
  padding: 0 5px;
  border: none;
  width: 45px;
  min-width: 45px;
  border-radius: 5px;

  text-align: center;
`;
const ColorTextAction = () => {
  const dispatch = useDesignerUIContext(c => c.dispatch);
  const handleFontColorClick = useCallback(() => {
    dispatch({
      type: DesignerUIStateActions.setSelectedTab,
      payload: {
        tab: 'colorPicker',
      },
    });
  }, [dispatch]);

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <ToolbarButtonContainer className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}>
        <Tooltip title={'Change color'} mouseEnterDelay={1}>
          <PreventUnselectOverlay
            className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
            onClick={handleFontColorClick}
            onTouchEnd={handleFontColorClick}
          />
        </Tooltip>

        <IconWrapper height="20px" width="20px">
          <FontColorSvg />
        </IconWrapper>
      </ToolbarButtonContainer>
      <RightSideVerticalDivider />
    </div>
  );
};
const EmojiAction = ({
  elements,
  richTextEditor,
  editingTextElementId,
}: {
  elements: SlideElement[];
  richTextEditor: RichTextEditorType | null;
  editingTextElementId?: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleEmojiClick = useCallback(
    (emoji: EmojiClickData) => {
      if (elements.length === 0) return;
      if (elements.length === 1 && richTextEditor && editingTextElementId) {
        toggleEmojiPicked(richTextEditor, emoji);
        return;
      }
    },
    [editingTextElementId, elements, richTextEditor],
  );

  const emojiMenu = useCallback(
    () => (
      <div className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}>
        <EmojiPicker
          width={'250px'}
          height={'300px'}
          lazyLoadEmojis={true}
          searchDisabled={true}
          skinTonesDisabled={false}
          suggestedEmojisMode={'recent' as SuggestionMode}
          autoFocusSearch={false}
          onEmojiClick={(emoji: EmojiClickData) => {
            handleEmojiClick(emoji);
            setIsVisible(false);
          }}
        />
      </div>
    ),
    [handleEmojiClick],
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <ToolbarButtonContainer className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}>
        <Dropdown overlay={emojiMenu} placement="bottomCenter" visible={isVisible}>
          <a>
            <PreventUnselectOverlay
              className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
              onClick={() => setIsVisible(true)}
              onTouchEnd={() => setIsVisible(true)}
            />
            <IconWrapper>
              <EmojiSvg />
            </IconWrapper>
          </a>
        </Dropdown>
      </ToolbarButtonContainer>
      <RightSideVerticalDivider />
    </div>
  );
};

// ----- TOOLBAR ------
const ToolbarButton = ({
  icon,
  onClick,
  isDisabled,
  tooltip,
  isActive,
  preventDefault,
  style,
  iconWidth = 15,
  iconHeight = 15,
  metric,
  isMiniToolbar,
}: {
  icon: ReactNode;
  onClick: () => void;
  isDisabled?: boolean;
  tooltip: string;
  isActive?: boolean;
  preventDefault?: boolean;
  style?: CSSProperties;
  iconWidth?: number;
  iconHeight?: number;
  metric?: string;
  isMiniToolbar?: boolean;
}) => {
  const metrics = useMetrics();
  return (
    <ToolbarButtonContainer
      $isDisabled={isDisabled}
      style={style}
      $isActive={isActive}
      $isMiniToolbar={isMiniToolbar}
      className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
    >
      <Tooltip title={tooltip} mouseEnterDelay={1}>
        <PreventUnselectOverlay
          $height={isMiniToolbar ? 15 : 30}
          $width={isMiniToolbar ? 15 : 30}
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          onTouchEnd={e => {
            if (metric) metrics.logEvent(metric);
            if (preventDefault) {
              e.preventDefault();
            }
            if (isDisabled) return;
            onClick();
          }}
          onClick={e => {
            if (metric) metrics.logEvent(metric);
            if (preventDefault) {
              e.preventDefault();
            }
            if (isDisabled) return;
            onClick();
          }}
        />
      </Tooltip>
      <div style={{ padding: isMiniToolbar ? '0px' : '5px' }}>
        <IconWrapper
          className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
          width={`${iconWidth}px`}
          height={`${iconHeight}px`}
        >
          {' '}
          {icon}
        </IconWrapper>
      </div>
    </ToolbarButtonContainer>
  );
};
const ToolbarButtonContainer = styled.div<{
  $isDisabled?: boolean;
  $isActive?: boolean;
  $isMiniToolbar?: boolean;
}>`
  position: relative;
  height: 30px;
  width: 30px;
  display: flex;
  justify-content: center;
  .ant-dropdown-trigger {
    width: 30px !important;
  }
  align-items: center;
  border-radius: 5px;
  cursor: pointer;
  ${({ $isMiniToolbar }) =>
    $isMiniToolbar
      ? css`
          &:hover {
            background-color: ${theme.colors.white};
          }
        `
      : `
    &:hover {
      background-color: ${theme.colors.grey};
    }`}
 

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.3;
      box-shadow: ${theme.effects.disabledElementShadow};
      cursor: default;
    `}
  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: ${theme.colors.grey};
    `}
  // allows the whole button to be clickable
  .ant-dropdown-trigger{
    height: 30px !important;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const PreventUnselectOverlay = styled.div<{ $height?: number; $width?: number }>`
  position: absolute;
  z-index: ${zIndexes('CREATOR_SLIDE_EDITOR_TOOLBAR_OVERLAY')};
  height: ${({ $height }) => $height || 30}px;
  width: ${({ $width }) => $width || 30}px;
  cursor: pointer;
`;

const RightSideVerticalDivider = styled.div`
  height: 20px;
  width: 0px;
  margin: 0 0 0 10px;
  border-left: solid 1px ${theme.colors.grey};
`;

export default SlideEditorToolbar;
